// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { auth } from '../services/firebase/config';
import { getUserData, validateAndSyncStreak } from '../services/firebase/firestoreService';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const validateUserData = (data) => {
    if (!data) return null;
    return {
      ...data,
      fitnessCurrentDay: Math.max(1, data.fitnessCurrentDay || 1),
      streakCount: Math.max(0, data.streakCount || 0),
      fitnessPoints: Math.max(0, data.fitnessPoints || 0)
    };
  };

  // Set persistence once on mount
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch(() => {});
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        try {
          const data = await getUserData(user.uid);

          if (data?.status === 'inactive') {
            await signOut(auth);
            setCurrentUser(null);
            setUserData(null);
            setLoading(false);
            return;
          }

          if (data && data.role !== 'admin') {
            const correctStreak = await validateAndSyncStreak(user.uid, data);
            data.streakCount = correctStreak;
          }

          setUserData(validateUserData(data));
        } catch (e) {
          setUserData(null);
        }
      } else {
        setUserData(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (phone, password) => {
    const cleanPhone = phone.replace(/[\s+\-()]/g, '').slice(-10);
    const email = `${cleanPhone}@spocademy.com`;
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const data = await getUserData(userCredential.user.uid);

    if (data?.status === 'inactive') {
      await signOut(auth);
      throw new Error('ACCOUNT_INACTIVE');
    }

    if (data && data.role !== 'admin') {
      const correctStreak = await validateAndSyncStreak(userCredential.user.uid, data);
      data.streakCount = correctStreak;
    }

    localStorage.setItem('spocademy_language', 'mr');
    setUserData(validateUserData(data));
    return userCredential;
  };

  const logout = async () => {
    await signOut(auth);
    setUserData(null);
  };

  const refreshUserData = async () => {
    if (!currentUser) return;
    try {
      const data = await getUserData(currentUser.uid);
      if (data?.status === 'inactive') {
        await logout();
        return;
      }
      setUserData(validateUserData(data));
    } catch (e) {
      // silently continue
    }
  };

  return (
    <AuthContext.Provider value={{ currentUser, userData, loading, login, logout, refreshUserData }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};